import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";

/**
 * REP-211 — concierge property discovery.
 *
 * `POST /api/properties/discover` powers the assistant's "Find a property"
 * criteria box. All criteria are optional; the backend returns scored
 * `PropertyResultDTO`s (lighter than the full property record) that the
 * assistant renders as the existing property entity cards.
 */
export interface DiscoverPropertiesRequest {
  query?: string;
  propertyType?: string;
  zoning?: string;
  city?: string;
  postalCode?: string;
  minSqft?: number;
  maxSqft?: number;
  minPrice?: number;
  maxPrice?: number;
  limit?: number;
  /** Optional client-need context for fit scoring. */
  needId?: string;
}

export interface PropertyResultDTO {
  id: string;
  type: "property";
  name: string;
  city?: string;
  asset?: string;
  sf?: string;
  price?: string;
  zoning?: string;
  broker?: string;
  lat?: number;
  lng?: number;
  /** 0–100 fit score against the supplied criteria / need. */
  fitPct?: number;
  fitReason?: string;
}

export const propertiesApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "propertiesApi",
    baseUrl: "/api/properties",
  }),
  reducerPath: "propertiesApi",
  tagTypes: ["Properties"],
  endpoints: (builder) => ({
    getProperties: builder.query<
      any,
      {
        include?: string;
        search?: string;
        nextId?: string;
        limit?: number;
        propertyType?: string;
        state?: string;
        city?: string;
        postalCode?: string;
        country?: string;
        officeClass?: string;
        yearBuiltMin?: number;
        yearBuiltMax?: number;
        squareFeetMin?: number;
        squareFeetMax?: number;
        assessedValueMin?: number;
        assessedValueMax?: number;
        zoningType?: string;
        zoningSubtype?: string;
        radiusMiles?: number;
        boundsNorth?: number;
        boundsSouth?: number;
        boundsEast?: number;
        boundsWest?: number;
        // REP-206 §4 — "Mine" map toggle. The backend maps `myProperties`
        // → `ownerId = personId` and sets `skipBounds`, so the caller only
        // needs to flip this on; the bounds args are ignored server-side.
        myProperties?: boolean;
        favorites?: boolean;
        [key: string]: any; // Allow additional filter parameters
      }
    >({
      // B6 — Properties page pans the map a lot; without a hold time RTK
      // tears down each query the moment it goes unused, so every nudge
      // refetches. 60s gives the user time to scrub back to a previously
      // panned area and hit cache.
      keepUnusedDataFor: 60,
      serializeQueryArgs: ({ queryArgs }) => {
        // Stable serialization to prevent duplicate requests. Bounds are
        // rounded inside `query` to 3 decimal places (~110m) — round them
        // here too so adjacent panned positions hit the same cache key.
        const args = { ...queryArgs } as Record<string, any>;
        const roundIfBound = (k: string) => {
          if (args[k] != null && args[k] !== "") {
            const n = Number(args[k]);
            if (Number.isFinite(n)) args[k] = Number(n.toFixed(3));
          }
        };
        roundIfBound("boundsNorth");
        roundIfBound("boundsSouth");
        roundIfBound("boundsEast");
        roundIfBound("boundsWest");
        return JSON.stringify(args);
      },
      query: (args) => {
        const queryParams = new URLSearchParams();

        // `search` is the legacy arg name — backend reads `query=`.
        if (args.search) queryParams.append("query", args.search);

        // Bounds are pan-driven and re-emit on every frame of inertial
        // scroll. Round to 3 decimal places (~110m at the equator) so
        // adjacent fetches collapse onto the same cache key and don't
        // trigger redundant requests every pixel of pan.
        const BOUND_KEYS = new Set(["boundsNorth", "boundsSouth", "boundsEast", "boundsWest"]);

        // Pass through every other defined arg as-is. The TS signature
        // is `[key: string]: any`, so the caller can pass any backend
        // filter (REP-200 filters: hasParking, use, sqftMin/Max,
        // priceMin/Max, listingStatus, propertyIds, etc.). Treat empty
        // string / null / undefined as "skip"; everything else stringifies.
        for (const [key, value] of Object.entries(args)) {
          if (key === "search") continue; // handled above
          if (value === undefined || value === null || value === "") continue;
          // REP-206 §4 — boolean flags (myProperties / favorites) are
          // opt-in: only serialize the `true` case so callers can pass
          // `false` without polluting the URL or busting the cache key.
          if (value === false) continue;
          if (BOUND_KEYS.has(key)) {
            const n = Number(value);
            if (!Number.isFinite(n)) continue;
            queryParams.append(key, n.toFixed(3));
          } else {
            queryParams.append(key, String(value));
          }
        }

        return `?${queryParams.toString()}`;
      },
      providesTags: (result) =>
        result
          ? [
              ...result?.data?.data?.map(
                ({ id }: any) => ({ type: "Properties", id } as const)
              ),
              "Properties",
            ]
          : ["Properties"],
    }),
    getPropertyById: builder.query<any, { id?: string; include?: string }>({
      query: (args) => {
        const queryParams = args.include ? `?include=${args.include}` : "";
        return `/${args.id}${queryParams}`;
      },
      providesTags: (_result, _error, args) => [{ type: "Properties", id: args.id }],
    }),
    createProperty: builder.mutation<any, any>({
      query: (body) => ({
        url: "/",
        method: "POST",
        body: body,
      }),
      invalidatesTags: [{ type: "Properties" }],
    }),
    updateProperty: builder.mutation<any, { id: string; updates: any }>({
      query: ({ id, updates }) => ({
        url: `/${id}`,
        method: "PATCH",
        body: updates,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Properties", id },
        { type: "Properties", id: "LIST" },
      ],
    }),
    deleteProperty: builder.mutation<void, string>({
      query: (id) => ({
        url: `/${id}`,
        method: "DELETE",
      }),
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
        // Optimistically update the cache
        const patchResult = dispatch(
          propertiesApi.util.updateQueryData(
            "getProperties",
            { include: "propertyAssets,propertyData" },
            (draft: any) => {
              return {
                ...draft,
                data: draft.data.filter((property: any) => property.id !== id),
              };
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: [{ type: "Properties", id: "LIST" }],
    }),

    updatePropertyData: builder.mutation<
      any,
      { id: string; updates: Record<string, any> }
    >({
      query: ({ id, updates }) => ({
        url: `/${id}/data`,
        method: "PATCH",
        body: updates,
      }),
      invalidatesTags: ({ id }) => [
        { type: "Properties", id },
        { type: "Properties", id: "LIST" },
      ],
    }),
    getFilterOptions: builder.query<any, void>({
      query: () => "/filter-options",
      providesTags: ["Properties"],
    }),
    addPropertyImage: builder.mutation<
      any,
      { propertyId: string; assetRef: string; type?: string; visibility?: "STAGED" | "LIVE" }
    >({
      query: ({ propertyId, assetRef, type = "PREVIEW", visibility }) => ({
        url: `/${propertyId}/images`,
        method: "POST",
        body: { assetRef, type, ...(visibility ? { visibility } : {}) },
      }),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: "Properties", id: propertyId },
      ],
    }),
    // REP-206 §4 — Property DRAFT MODE controls.
    publishProperty: builder.mutation<any, { propertyId: string }>({
      query: ({ propertyId }) => ({
        url: `/${propertyId}/publish`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: "Properties", id: propertyId },
      ],
    }),
    unpublishProperty: builder.mutation<any, { propertyId: string }>({
      query: ({ propertyId }) => ({
        url: `/${propertyId}/unpublish`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: "Properties", id: propertyId },
      ],
    }),
    setPropertyAssetVisibility: builder.mutation<
      any,
      { propertyId: string; assetId: string; visibility: "STAGED" | "LIVE" }
    >({
      query: ({ propertyId, assetId, visibility }) => ({
        url: `/${propertyId}/assets/${assetId}/visibility`,
        method: "PATCH",
        body: { visibility },
      }),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: "Properties", id: propertyId },
      ],
    }),
    addPropertyAsset: builder.mutation<
      any,
      { propertyId: string; assetRef: string; fileName: string; fileType: string; type?: string }
    >({
      query: ({ propertyId, assetRef, fileName, fileType, type = "DOCUMENT" }) => ({
        url: `/${propertyId}/assets`,
        method: "POST",
        body: { assetRef, fileName, fileType, type },
      }),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: "Properties", id: propertyId },
      ],
    }),
    addPropertyNote: builder.mutation<
      any,
      { propertyId: string; text: string }
    >({
      query: ({ propertyId, text }) => ({
        url: `/${propertyId}/notes`,
        method: "POST",
        body: { text },
      }),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: "Properties", id: propertyId },
      ],
    }),

    // REP-200 §7 — prospecting notes
    getPropertyNotes: builder.query<
      any,
      { propertyId: string; visibility?: "PRIVATE" | "TOUR" | "DEAL"; authorId?: string }
    >({
      query: ({ propertyId, visibility, authorId }) => {
        const qs = new URLSearchParams();
        if (visibility) qs.append("visibility", visibility);
        if (authorId) qs.append("authorId", authorId);
        const query = qs.toString();
        return `/${propertyId}/notes${query ? `?${query}` : ""}`;
      },
      providesTags: (_result, _error, { propertyId }) => [
        { type: "Properties", id: `${propertyId}:notes` },
      ],
    }),
    upsertPropertyNote: builder.mutation<
      any,
      { propertyId: string; text: string; visibility?: "PRIVATE" | "TOUR" | "DEAL" }
    >({
      query: ({ propertyId, text, visibility }) => ({
        url: `/${propertyId}/notes`,
        method: "POST",
        body: { text, visibility: visibility ?? "PRIVATE" },
      }),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: "Properties", id: propertyId },
        { type: "Properties", id: `${propertyId}:notes` },
      ],
    }),
    deletePropertyNote: builder.mutation<
      any,
      { propertyId: string; noteId: string }
    >({
      query: ({ propertyId, noteId }) => ({
        url: `/${propertyId}/notes/${noteId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: "Properties", id: propertyId },
        { type: "Properties", id: `${propertyId}:notes` },
      ],
    }),

    // REP-200 §5 — paste-addresses resolver
    pasteResolveAddresses: builder.mutation<
      {
        data: {
          matched: Array<{ raw: string; propertyId: string; normalizedAddress: string }>;
          unmatched: Array<{ raw: string; reason: string; normalizedAddress?: string }>;
        };
      },
      { addresses: string[]; listId?: string }
    >({
      query: (body) => ({
        url: "/paste-resolve",
        method: "POST",
        body,
      }),
    }),
    generateMap: builder.mutation<any, { propertyId: string }>({
      query: ({ propertyId }) => ({
        url: `/${propertyId}/generate-map`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: "Properties", id: propertyId },
      ],
    }),
    mergeProperties: builder.mutation<
      any,
      { primaryPropertyId: string; secondaryPropertyId: string }
    >({
      query: ({ primaryPropertyId, secondaryPropertyId }) => ({
        url: `/${primaryPropertyId}/merge`,
        method: "POST",
        body: { secondaryPropertyId },
      }),
      invalidatesTags: (_result, _error, { primaryPropertyId }) => [
        { type: "Properties", id: primaryPropertyId },
        { type: "Properties" },
      ],
    }),
    requestOwnership: builder.mutation<
      any,
      { propertyId: string; reason?: string }
    >({
      query: ({ propertyId, reason }) => ({
        url: `/${propertyId}/claim`,
        method: "POST",
        body: { reason },
      }),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: "Properties", id: propertyId },
      ],
    }),
    toggleFavorite: builder.mutation<any, { propertyId: string; include?: string }>({
      query: ({ propertyId }) => ({
        url: `/${propertyId}/favorite`,
        method: "POST",
      }),
      // Optimistically flip the heart on the detail-page cache so it turns
      // red immediately, then reconcile against the server via the
      // invalidation-driven refetch below (or roll back on error). The
      // optional `include` lets the caller target the exact cache entry the
      // detail page subscribed to.
      onQueryStarted: async (
        { propertyId, include },
        { dispatch, queryFulfilled }
      ) => {
        const patch = dispatch(
          propertiesApi.util.updateQueryData(
            "getPropertyById",
            { id: propertyId, include },
            (draft: { data?: { isFavorited?: boolean } }) => {
              if (draft?.data) {
                draft.data.isFavorited = !draft.data.isFavorited;
              }
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: "Properties", id: propertyId },
        { type: "Properties", id: "FAVORITES" },
      ],
    }),
    getBatchFavoriteStatus: builder.query<any, void>({
      query: () => "/favorites/status",
      providesTags: [{ type: "Properties", id: "FAVORITES" }],
    }),
    getFavoriteStatus: builder.query<any, { propertyId: string }>({
      query: ({ propertyId }) => `/${propertyId}/favorite`,
      providesTags: (_result, _error, { propertyId }) => [
        { type: "Properties", id: propertyId },
      ],
    }),
    getNextFeatureGroupIndex: builder.query<any, { propertyId: string; sectionKey: string }>({
      query: ({ propertyId, sectionKey }) => `/${propertyId}/features/next-group-index?sectionKey=${sectionKey}`,
    }),
    getPropertyFeatures: builder.query<any, { propertyId: string }>({
      query: ({ propertyId }) => `/${propertyId}/features`,
      providesTags: (_result, _error, { propertyId }) => [
        { type: "Properties", id: propertyId },
      ],
    }),
    upsertFeatureField: builder.mutation<
      any,
      { propertyId: string; sectionKey: string; groupIndex: number; fieldKey: string; value?: string | null; assetRef?: string | null; fileName?: string | null; fileType?: string | null }
    >({
      query: ({ propertyId, ...body }) => ({
        url: `/${propertyId}/features/field`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: "Properties", id: propertyId },
      ],
    }),
    syncFeatureFieldValues: builder.mutation<
      any,
      { propertyId: string; sectionKey: string; fieldKey: string; values: string[] }
    >({
      query: ({ propertyId, ...body }) => ({
        url: `/${propertyId}/features/field-values`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: "Properties", id: propertyId },
      ],
    }),
    deleteFeatureEntry: builder.mutation<any, { propertyId: string; entryId: string }>({
      query: ({ propertyId, entryId }) => ({
        url: `/${propertyId}/features/entry/${entryId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: "Properties", id: propertyId },
      ],
    }),
    deleteFeatureGroup: builder.mutation<any, { propertyId: string; sectionKey: string; groupIndex: number }>({
      query: ({ propertyId, ...body }) => ({
        url: `/${propertyId}/features/group`,
        method: "DELETE",
        body,
      }),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: "Properties", id: propertyId },
      ],
    }),

    // REP-211 — concierge "Find a property" discovery. Read-only ranking, so
    // it neither provides nor invalidates Properties cache tags.
    discoverProperties: builder.mutation<
      { data: PropertyResultDTO[] },
      DiscoverPropertiesRequest
    >({
      query: (body) => ({
        url: "/discover",
        method: "POST",
        body,
      }),
    }),
  }),
});

export const {
  useGetPropertiesQuery,
  useLazyGetPropertiesQuery,
  useGetPropertyByIdQuery,
  useCreatePropertyMutation,
  useUpdatePropertyMutation,
  useDeletePropertyMutation,
  useUpdatePropertyDataMutation,
  useGetFilterOptionsQuery,
  useAddPropertyImageMutation,
  useAddPropertyAssetMutation,
  // REP-206 §4 — Property DRAFT MODE
  usePublishPropertyMutation,
  useUnpublishPropertyMutation,
  useSetPropertyAssetVisibilityMutation,
  useAddPropertyNoteMutation,
  useGenerateMapMutation,
  useMergePropertiesMutation,
  useRequestOwnershipMutation,
  useToggleFavoriteMutation,
  useGetBatchFavoriteStatusQuery,
  useGetFavoriteStatusQuery,
  useLazyGetNextFeatureGroupIndexQuery,
  useGetPropertyFeaturesQuery,
  useUpsertFeatureFieldMutation,
  useSyncFeatureFieldValuesMutation,
  useDeleteFeatureEntryMutation,
  useDeleteFeatureGroupMutation,
  // REP-200
  useGetPropertyNotesQuery,
  useUpsertPropertyNoteMutation,
  useDeletePropertyNoteMutation,
  usePasteResolveAddressesMutation,
  // REP-211 — concierge discovery
  useDiscoverPropertiesMutation,
} = propertiesApi;

/* Public property endpoint — no auth required */
export const publicPropertiesApi = createApi({
  reducerPath: "publicPropertiesApi",
  baseQuery: fetchBaseQuery({ baseUrl: "/api/properties/public" }),
  endpoints: (builder) => ({
    getPublicPropertyById: builder.query<any, { id: string }>({
      query: (args) => `/${args.id}`,
    }),
  }),
});

export const { useGetPublicPropertyByIdQuery } = publicPropertiesApi;
